import { Injectable } from '@angular/core';
import { ReturnPartServiceBase } from '../../Models/returnPartServiceBase';
import { ReturnService } from '../../return.service';
import { AssessorFactorsEnum, ReturnAssetsServiceSharedState } from '../Assets/returnAssets.service';
import { ReturnAssetRepository, ReturnRepository } from '../../../Repositories';
import { ScheduleAdditionsDisposalsReconciliationReport } from '../../Models/scheduleAdditionsDisposalsReconciliationReport';
import { ReturnSummaryReconciliationReport } from '../../Models/returnSummaryReconciliationReport';
import { Subject, Observable, lastValueFrom } from 'rxjs';
import { FilingControlReconciliationReport } from '../../Models/filingControlReconciliationReport';
import { ReconciliationReport } from '../../Models/reconciliationReport';
import { ReturnPreviewServiceSharedState } from '../Preview/returnPreview.service';

export interface ReturnOverviewServiceSharedState {
    reconciliationReport: ReconciliationReport;
}

export enum FilingControlGroupBy {
    None,
    Assessor,
    Consolidated
}

@Injectable()
export class ReturnOverviewService extends ReturnPartServiceBase {
    constructor(
        private readonly _returnRepository: ReturnRepository,
        private readonly _returnAssetRepository: ReturnAssetRepository,
        private readonly _returnService: ReturnService
    ) {
        super();
    }

    private _sharedState: ReturnOverviewServiceSharedState = {
        reconciliationReport: null
    } as ReturnOverviewServiceSharedState;

    private readonly returnSummaryReconciliationReport: ReconciliationReport = {
        name: 'Return Summary',
        value: 0,
        allowPrimaryForReturn: true
    };

    private readonly scheduleAdditionsDisposalsReconciliationReport: ReconciliationReport = {
        name: 'Schedule Additions and Disposals',
        value: 1,
        allowPrimaryForReturn: false
    };

    private readonly filingControlReconciliationReport: ReconciliationReport = {
        name: 'Filing Control',
        value: 2,
        allowPrimaryForReturn: true
    };

    private _reconciliationReports: ReconciliationReport[] = [
        this.returnSummaryReconciliationReport,
        this.scheduleAdditionsDisposalsReconciliationReport,
        this.filingControlReconciliationReport
    ];

    private _reconciliationReportSubject: Subject<ReconciliationReport> = new Subject();
    private _returnAssetsSelectedSubject: Subject<ReturnAssetsServiceSharedState> = new Subject();
    private _returnPreviewSelectedSubject: Subject<ReturnPreviewServiceSharedState> = new Subject();

    get sharedState(): ReturnOverviewServiceSharedState {
        return this._sharedState;
    }

    get reconciliationReports(): ReconciliationReport[] {
        return this._reconciliationReports;
    }

    get availableReconciliationReports(): ReconciliationReport[] {
        return this._reconciliationReports.filter(x => x.allowPrimaryForReturn || !!this._returnService.getUniqueAssociatedReturnFormRevisions().length);
    }

    get reconciliationReport$(): Observable<ReconciliationReport> {
        return this._reconciliationReportSubject.asObservable();
    }

    get returnAssetsSelected$(): Observable<ReturnAssetsServiceSharedState> {
        return this._returnAssetsSelectedSubject.asObservable();
    }

    get returnPreviewSelected$(): Observable<ReturnPreviewServiceSharedState> {
        return this._returnPreviewSelectedSubject.asObservable();
    }

    initialize(): void {
        this._sharedState = {
            reconciliationReport: null
        } as ReturnOverviewServiceSharedState;
    }

    setReconciliationReport(reconciliationReport: ReconciliationReport): void {
        this._sharedState.reconciliationReport = reconciliationReport;

        // if the report doesn't support primary for return and primary for return is selected, select a form
        if ((!reconciliationReport.allowPrimaryForReturn) && (!this._returnService.sharedState.formRevisionId)) {
            this._returnService.setFirstFormRevisionId();
        }

        this._reconciliationReportSubject.next(this._sharedState.reconciliationReport);
    }

    notifyReturnAssetsSelected(value: ReturnAssetsServiceSharedState): void {
        this._returnAssetsSelectedSubject.next(value);
    }

    notifyReturnPreviewSelected(value: ReturnPreviewServiceSharedState): void {
        this._returnPreviewSelectedSubject.next(value);
    }

    async getScheduleAdditionsDisposalReconciliationReport(): Promise<ScheduleAdditionsDisposalsReconciliationReport> {
        // the schedule summary returns information about assets not assigned to a schedule
        // using this API call to put together a list of all the schedules that needs to be passed to schedule details
        const searchModel = {
            assessorFilter: AssessorFactorsEnum.AnyAssessor,
            formRevisionIds: [this._returnService.sharedState.formRevisionId],
            parcelIds: this._returnService.sharedState.returns.map(x => x.parcelId),
            priorReturnStatuses: [],
            aggregationLevel: Compliance.ScheduleDetailsAggregationLevelEnum.Schedule,
            includeMergedParcels: this._returnService.isConsolidatedReturn
        } as Compliance.ReturnAssetScheduleDetailsSearchModel;

        const responses = await Promise.all([
            lastValueFrom(this._returnAssetRepository.getScheduleDetailsByFilingBatch(this._returnService.filingBatchId, searchModel)),
            lastValueFrom(this._returnAssetRepository.getUnassignedScheduleDetailsByFilingBatch(this._returnService.filingBatchId, searchModel))
        ]);

        const assignedScheduleDetails = responses[0] as Compliance.ReturnAssetScheduleDetailsAssignedItemModel[];
        const unassignedScheduleDetails: Compliance.ReturnAssetScheduleDetailsUnassignedItemModel[] = responses[1];

        return new ScheduleAdditionsDisposalsReconciliationReport(assignedScheduleDetails, unassignedScheduleDetails);
    }

    /**
     * Builds the return summary reconciliation report for the current working set.
     *
     * The filing batch prepared data ships with a summary covering the whole batch with no form selected,
     * which lets the initial load skip a round trip.
     * @param force Pass true when the caller knows something has changed (ReturnUpdateLogicService owns
     * that change detection) and the summary must come from the server. Passing false is only a hint: the
     * prepared summary is still validated against this request before it is used, so a caller that gets
     * the flag wrong cannot serve a stale summary.
     */
    async getReturnSummaryReconciliationReport(force: boolean = false): Promise<ReturnSummaryReconciliationReport> {
        const formRevisionId = this._returnService.sharedState.formRevisionId;
        const inUseReturnFormRevisions = this._returnService.getAssociatedReturnFormRevisions();
        const formRevisions = inUseReturnFormRevisions.filter(x => x.formRevisionId === formRevisionId);

        // if a specific form is selected, limit the parcels to the ones using that form,
        // otherwise (primary for return) use every parcel in the current working set
        const parcelIds = (formRevisions.length)
            ? this._returnService.sharedState.returns
                .filter(x => formRevisions.some(y => y.returnId === x.returnId))
                .map(x => x.parcelId)
            : this._returnService.sharedState.returns.map(x => x.parcelId);

        // a forced refresh means the data behind the prepared summary has moved on, so drop it for good
        if (force) {
            this._returnService.invalidatePreparedReturnSummary();
        }

        // the prepared summary is only handed back when it is genuinely valid for this request, i.e. no
        // form is selected and the requested parcels still match the parcels it was calculated for
        let returnSummaryResult = this._returnService.takePreparedReturnSummary(formRevisionId, parcelIds);

        if (!returnSummaryResult) {
            returnSummaryResult = await lastValueFrom(this._returnRepository.getReturnSummary(
                this._returnService.filingBatchId,
                formRevisionId,
                parcelIds
            ));
        }

        return new ReturnSummaryReconciliationReport(returnSummaryResult);
    }

    async getFilingControlReconciliationReport(): Promise<FilingControlReconciliationReport> {
        const filingControlSummaryResult: Compliance.ReturnFilingControlSummaryModel = await lastValueFrom(this._returnRepository.getReturnFilingControlSummary(
            this._returnService.filingBatchId,
            this._returnService.sharedState.formRevisionId,
            this._returnService.sharedState.returns.map(x => x.parcelId)
        ));

        return new FilingControlReconciliationReport(filingControlSummaryResult, this._returnService.getAssociatedReturnFormRevisions(),
            this._returnService.sharedState.returns);
    }
}
